import axios from 'axios';

// Helper function to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Helper function to check session storage
function getSessionData(key) {
  try {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

// Main authentication status checker
export async function checkUserAuthStatus(brandId = 'LAX') {
  // Check local indicators first (fast check)
  const authToken = getCookie('authToken');
  const uuid = getSessionData('uuid');
  const sessionUserData = getSessionData('session_userdata');
  
  if (!authToken && !uuid) {
    return {
      isAuthenticated: false,
      user: null,
      authMethod: null
    };
  }

  // If we have session data, return it (assuming it's valid)
  if (sessionUserData && uuid) {
    return {
      isAuthenticated: true,
      user: sessionUserData,
      authMethod: authToken ? 'webauthn' : 'otp',
      uuid: uuid
    };
  }

  // If we only have authToken, verify it with server
  if (authToken) {
    try {
      const isValid = await verifyAuthToken(authToken);
      if (isValid.valid) {
        // Fetch and store user details
        const userDetails = await handleUserLogin(isValid.uuid, brandId);
        return {
          isAuthenticated: true,
          user: userDetails,
          authMethod: 'webauthn',
          uuid: isValid.uuid
        };
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      // Clear invalid token
      document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
  }

  return {
    isAuthenticated: false,
    user: null,
    authMethod: null
  };
}

// Verify auth token with server (you can implement this as a Lambda)
async function verifyAuthToken(token) {
  try {
    const response = await axios.post(process.env.VUE_APP_VERIFY_TOKEN_URL || '/verify-token', {
      token: token
    });
    return response.data;
  } catch (error) {
    throw new Error('Token verification failed');
  }
}

// Logout function
export function logout() {
  // Clear cookie
  document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; samesite=strict';
  
  // Clear session storage
  sessionStorage.removeItem('uuid');
  sessionStorage.removeItem('session_userdata');
  sessionStorage.removeItem('session_uuid');
  
  // Reload page to reset state
  window.location.reload();
}

// Handle user login - fetch user details and store in session
async function handleUserLogin(uuid, brandId) {
  const genkeys = (await import('rezauthlib')).default;
  
  const isHER = brandId === "HER";
  const secsaa = genkeys(
    isHER
      ? process.env.VUE_APP_USERDET_SECRETID_FRA
      : process.env.VUE_APP_USERDET_SECRETID,
    isHER
      ? process.env.VUE_APP_USERDET_SECRETKEY_FRA
      : process.env.VUE_APP_USERDET_SECRETKEY
  );

  const ur = isHER
    ? process.env.VUE_APP_DOMAIN_DEVAWS_FRA
    : process.env.VUE_APP_DOMAIN_DEVAWS;

  const response = await axios.post(
    ur + "userdetails/",
    { slug: brandId, uid: uuid },
    {
      headers: {
        b1: secsaa.b1s,
        v2: secsaa.v2s,
        c2s: secsaa.c2s,
        a1: secsaa.a1,
        v1: secsaa.v1,
        c1: secsaa.uidgen,
      },
    }
  );

  const userDetails = JSON.parse(response.request.response);
  
  if (userDetails.user_exists === "Y") {
    const userData = userDetails.result[0];
    
    // Store in session storage
    sessionStorage.setItem("session_uuid", userData.uuid);
    sessionStorage.setItem("uuid", JSON.stringify(uuid));
    sessionStorage.setItem("session_userdata", JSON.stringify(userData));
    
    return userData;
  }
  
  throw new Error('User not found');
}

// Quick sync check (no server call)
export function isUserLoggedIn() {
  const authToken = getCookie('authToken');
  const uuid = getSessionData('uuid');
  const sessionUserData = getSessionData('session_userdata');
  
  return !!(authToken || (uuid && sessionUserData));
}